import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { PlanoDeContasService } from '../../services/plano-de-contas.service';
import { Location } from '@angular/common';
import { JwtHelper } from '../../helpers/jwtDecode.helper';
import { SpinnerService } from '../../services/spinner.service';
import { ToastService } from '../../services/toast.service';
import { SeveridadeToast } from '../../models/enums/severidade-toast.enum';
import * as ExcelJS from 'exceljs';
import { AuthService } from '../../services/auth.service';
import { Balancete } from '../../models/balancete.model';
import { descricaoTipoTransacaoContabil, TipoTransacaoContabil } from '../../models/enums/tipoTransacaoContabil.enum';
import { MessageService } from 'primeng/api';
import { FormataValorPositivo } from '../../helpers/formata-valor-positivo.helper';
import { Icones } from '../../shared/variaveisDeRotas';
import { retornaDescricao } from '../../shared/mock/descricao';

@Component({
  selector: 'app-balancete',
  templateUrl: './balancete.component.html',
  styleUrls: ['./balancete.component.scss']
})
export class BalanceteComponent implements OnInit {
  @Output() tituloChange = new EventEmitter<string>();
  @Output() iconeChange = new EventEmitter<string>();

  @Output() tituloDescricao = new EventEmitter<string>();
  @Output() textoDescricao = new EventEmitter<string>();
  @Output() mostrarDescricao = new EventEmitter<boolean>();

  @ViewChild('dt') dt: Table | undefined;

  contasBalancete: Balancete[] = [];
  carregando: boolean = true;
  loading: boolean = false;
  private userId: string;
  dataInicio: Date | null = null;
  dataFim: Date | null = null;

  constructor(
    private planoDeContasService: PlanoDeContasService,
    private location: Location,
    private jwtHelper: JwtHelper,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
    private authService: AuthService,
    private messageService: MessageService,
    public formataValorPositivoHelper: FormataValorPositivo
  ) {
    this.userId = this.jwtHelper.getUserIdFromToken();
  }

  ngOnInit(): void {
    this.tituloChange.emit('Balancete');
    this.iconeChange.emit(Icones.listaTopicoAmarelo);
    this.tituloDescricao.emit(retornaDescricao('balancete').titulo);
    this.textoDescricao.emit(retornaDescricao('balancete').texto);
    this.mostrarDescricao.emit(true)
    this.definirDatasMesAtual();
    this.carregarRelatorioBalancete();
  }

  ngOnDestroy(): void {
    this.mostrarDescricao.emit(false);
  }

  definirDatasMesAtual(): void {
    const hoje = new Date();
    const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const ultimoDiaMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
    
    this.dataInicio = primeiroDiaMes;
    this.dataFim = ultimoDiaMes;
  }

  carregarRelatorioBalancete(): void {
    if (!this.dataInicio || !this.dataFim) {
      this.toastService.setMensagemToast('Selecione as datas de início e fim.', SeveridadeToast.Info);
      return;
    }

    this.loading = true;
    this.spinnerService.show('Carregando relatório de Balancete...');

    this.planoDeContasService.listarRelatorioBalancete(this.dataInicio, this.dataFim, this.userId).subscribe({
      next: (balancete) => {
        this.contasBalancete = balancete.map(conta => ({
          ...conta,
          descricaoTipoTransacaoSaldoAtual: this.getDescricaoNatureza(conta.naturezaSaldo),
          descricaoTipoTransacaoSaldoAnterior: this.getDescricaoNatureza(conta.naturezaSaldoAnterior)
        }));
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.toastService.setMensagemToast('Erro ao carregar relatório de Balancete.', SeveridadeToast.Error);
        console.error(err);
      },
      complete: () => {
        this.spinnerService.hide();
      }
    });
  }

  onDateChange(): void {
    if (this.dataInicio && this.dataFim) {
      this.clearToast();
      this.carregarRelatorioBalancete();
    }
  }

  clearToast(): void {
    this.messageService.clear();
  }

  applyFilterGlobal(value: string): void {
    if (this.dt) {
      this.dt.filterGlobal(value, 'contains');
    }
  }

  downloadExcel() {
    this.spinnerService.show('Gerando relatório Excel...');
    
    let dadosParaExportar = this.contasBalancete;
    
    if (this.dt && this.dt.filteredValue) {
      dadosParaExportar = this.dt.filteredValue;
    }

    this.authService.obterUsuarioPorId(this.userId).subscribe({
      next: (user) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Balancete');

        const tituloRow = worksheet.addRow(['Balancete de Contas Contábeis']);
        tituloRow.font = { bold: true, underline: true };
        worksheet.mergeCells(`A${tituloRow.number}:H${tituloRow.number}`);
        tituloRow.alignment = { vertical: 'middle', horizontal: 'center' };

        const cnpjUsuarioTexto = user.cpfCnpj ? `${user.cpfCnpj}` : 'CNPJ não encontrado';
        const dataInicioTexto = this.dataInicio
          ? this.dataInicio.toLocaleDateString('pt-BR', { year: 'numeric', month: '2-digit' })
          : 'Nenhuma Data Selecionada';
        const dataFimTexto = this.dataFim
          ? this.dataFim.toLocaleDateString('pt-BR', { year: 'numeric', month: '2-digit' })
          : 'Nenhuma Data Selecionada';

        const cnpjRow = worksheet.addRow(['CNPJ:', cnpjUsuarioTexto]);
        const inicioRow = worksheet.addRow(['Período Início:', dataInicioTexto]);
        const fimRow = worksheet.addRow(['Período Fim:', dataFimTexto]);

        [cnpjRow, inicioRow, fimRow].forEach(row => {
          row.getCell(1).font = { bold: true };
        });

        if (this.dt && this.dt.filteredValue && this.dt.filteredValue.length !== this.contasBalancete.length) {
          const filtroRow = worksheet.addRow(['Filtro de Pesquisa:', 'Aplicado']);
          filtroRow.getCell(1).font = { bold: true };
          const registrosRow = worksheet.addRow(['Registros Exportados:', `${dadosParaExportar.length} de ${this.contasBalancete.length}`]);
          registrosRow.getCell(1).font = { bold: true };
        }

        worksheet.addRow([]);

        const titleRow = worksheet.addRow([
          'CÓDIGO',
          'DESCRIÇÃO',
          'Saldo Anterior',
          'Natureza Saldo Anterior',
          'Débito',
          'Crédito',
          'Saldo Atual',
          'Natureza Saldo'
        ]);

        titleRow.font = { bold: true };
        titleRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD3D3D3' },
        };

        worksheet.columns = [
          { key: 'codigo', width: 40 },
          { key: 'descricao', width: 45 },
          { key: 'saldoAnterior', width: 20 },
          { key: 'naturezaSaldoAnterior', width: 10 },
          { key: 'debito', width: 25 },
          { key: 'credito', width: 25 },
          { key: 'saldoAtual', width: 25 },
          { key: 'naturezaSaldo', width: 10 },
        ];

        dadosParaExportar.forEach((conta, index) => {
          const row = worksheet.addRow({
            codigo: conta.codigo,
            descricao: conta.descricao,
            saldoAnterior: Math.abs(Number(conta.saldoAnterior) || 0),
            naturezaSaldoAnterior: conta.saldoAnterior !== 0 ? (conta.naturezaSaldoAnterior === TipoTransacaoContabil.Debito ? 'D' : 'C') : '',
            debito: Math.abs(Number(conta.debito) || 0),
            credito: Math.abs(Number(conta.credito) || 0),
            saldoAtual: Math.abs(Number(conta.saldoAtual) || 0),
            naturezaSaldo: conta.saldoAtual !== 0 ? (conta.naturezaSaldo === TipoTransacaoContabil.Debito ? 'D' : 'C') : ''
          });

          row.getCell(3).numFmt = '#,##0.00'; 
          row.getCell(5).numFmt = '#,##0.00'; 
          row.getCell(6).numFmt = '#,##0.00'; 
          row.getCell(7).numFmt = '#,##0.00'; 
        });



        workbook.xlsx.writeBuffer().then((buffer) => {
          const blob = new Blob([buffer], { type: 'application/octet-stream' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const dataHora = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
          a.download = `Relatório-Balancete-${dataHora}.xlsx`;
          
          a.click();
          window.URL.revokeObjectURL(url);

          this.spinnerService.hide();
          this.toastService.setMensagemToast('Relatório de Balancete gerado com sucesso!', SeveridadeToast.Success);
        }).catch((error) => {
          this.spinnerService.hide();
          this.toastService.setMensagemToast('Erro ao gerar relatório de Balancete.', SeveridadeToast.Error);
        });
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toastService.setMensagemToast('Erro Interno.', SeveridadeToast.Error);
      }
    });
  }

  voltar(): void {
    this.location.back();
  }

  private getDescricaoNatureza(natureza: TipoTransacaoContabil | null | undefined): string {
    if (natureza === null || natureza === undefined) {
      return '-';
    }
    
    switch (natureza) {
      case TipoTransacaoContabil.Debito:
        return 'Débito';
      case TipoTransacaoContabil.Credito:
        return 'Crédito';
      case TipoTransacaoContabil.Ajuste:
        return 'Ajuste';
      default:
        return '-';
    }
  }
}
